// bulbal: Shim đăng nhập tự động (SSO) qua phiên HRM.
//
// Luồng: fetch ticket từ HRM (cookie session, credentials:'include') -> connect() Tinode
// (nếu chưa kết nối) -> tinode.login('rest', ticket). Không throw ra ngoài: mọi lỗi trả về
// {ok:false, message} bằng tiếng Việt để UI hiện form đăng nhập thủ công + toast.
//
// QUAN TRỌNG (xác minh SDK tinode-sdk@0.25.3, xem BULBAL-FORK.md mục "Xác minh SDK"):
//   - Chữ ký thật của SDK là `tinode.login(scheme, secret, cred)` — 3 THAM SỐ VỊ TRÍ, KHÔNG
//     PHẢI `login({scheme, secret})` (object) như giả định ban đầu trong research/phase-02.
//   - SDK KHÔNG tự base64-encode `secret` cho scheme 'rest' — chỉ 'basic'/'reset' được SDK tự
//     b64EncodeUnicode() trong wrapper riêng (loginBasic/requestResetAuthSecret).
//   - HỆ QUẢ (sự cố thật 260729, phát hiện khi test UI bằng trình duyệt): server Tinode
//     unmarshal `login.secret` thành []byte theo chuẩn JSON của Go => chuỗi gửi lên PHẢI là
//     base64 hợp lệ. Gửi ticket THÔ (chứa dấu '.') => server trả
//     {"ctrl":{"code":400,"text":"malformed"}} và promise treo tới timeout 6s.
//     => Shim PHẢI tự btoa(ticket) trước khi login. HRM decode base64 rồi verify HMAC
//     (apps/web/lib/tinode/auth-handlers.ts::decodeSecretB64) — khớp đúng chiều.

import { BULBAL_HRM_TICKET_URL, BULBAL_SSO_FETCH_TIMEOUT_MS, BULBAL_SSO_MESSAGES } from './bulbal-config.js';

class HttpError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

// Gọi HRM lấy ticket. Trả về chuỗi ticket hoặc throw HttpError/Error với message tiếng Việt.
async function fetchHrmTicket() {
  const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), BULBAL_SSO_FETCH_TIMEOUT_MS) : null;

  let res;
  try {
    res = await fetch(BULBAL_HRM_TICKET_URL, {
      method: 'GET',
      credentials: 'include', // bắt buộc: cookie session khác subdomain (hrm.bulbalvn.com).
      signal: controller ? controller.signal : undefined,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') {
      throw new Error(BULBAL_SSO_MESSAGES.genericFail);
    }
    // Lỗi mạng (offline, DNS, CORS bị chặn...).
    throw new Error(BULBAL_SSO_MESSAGES.genericFail);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }

  // Log status only, never the response body/ticket. This safely identifies
  // expired sessions (401), inactive employees (403), and backend errors.
  console.info(`bulbal: HRM SSO ticket HTTP ${res.status}`);

  if (res.status === 401) {
    throw new HttpError(401, BULBAL_SSO_MESSAGES.sessionExpired);
  }
  if (res.status === 403) {
    // bulbal: phase-02 trả {error: '<tiếng Việt>'} khi tài khoản không active — ưu tiên dùng
    // nguyên văn message từ server nếu có, fallback về chuỗi cố định.
    let serverMsg;
    try {
      const body = await res.json();
      serverMsg = body && typeof body.error === 'string' ? body.error : undefined;
    } catch (_) {
      // body không phải JSON hợp lệ - bỏ qua, dùng fallback.
    }
    throw new HttpError(403, serverMsg || BULBAL_SSO_MESSAGES.notActiveEmployee);
  }
  if (!res.ok) {
    throw new HttpError(res.status, BULBAL_SSO_MESSAGES.genericFail);
  }

  let data;
  try {
    data = await res.json();
  } catch (_) {
    throw new Error(BULBAL_SSO_MESSAGES.genericFail);
  }
  if (!data || typeof data.ticket !== 'string' || !data.ticket) {
    throw new Error(BULBAL_SSO_MESSAGES.genericFail);
  }
  return data.ticket;
}

/**
 * bulbal: Thử đăng nhập tự động bằng ticket từ phiên HRM.
 * Chỉ gọi khi SDK CHƯA có token hợp lệ lưu sẵn trong localStorage (tự khôi phục phiên có ưu
 * tiên cao hơn, xem tinode-web.jsx componentDidMount).
 *
 * @param {import('tinode-sdk').Tinode} tinode - instance đã setup (đã hoặc chưa connect()).
 * @returns {Promise<{ok: boolean, message?: string}>} không bao giờ throw/reject.
 */
export async function trySsoLogin(tinode) {
  let ticket;
  try {
    ticket = await fetchHrmTicket();
  } catch (err) {
    return { ok: false, message: (err && err.message) || BULBAL_SSO_MESSAGES.genericFail };
  }

  try {
    if (!tinode.isConnected()) {
      await tinode.connect();
    }
    // bulbal: KHÔNG log ticket ra console (kể cả dev) — chỉ log message lỗi chung khi fail.
    // btoa = base64 CHUẨN (không phải base64url): bắt buộc, xem ghi chú đầu file.
    await tinode.login('rest', btoa(ticket));
    return { ok: true };
  } catch (err) {
    console.warn('bulbal: SSO login thất bại:', err && err.message);
    return { ok: false, message: BULBAL_SSO_MESSAGES.genericFail };
  }
}
